import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { capitalize } from 'lodash'
import { NodevarList } from 'v2/pages/flow/components/nodevar-list'
import type { RProviders } from 'v2/pages/flow/sidebar/types'
import {
  invalidSelectValueSchema,
  textSummarizationConfigSchema,
} from '../../config/validation.config'
import type { AiSkillsNodeData } from '../../types'
import { SkillInstructionsTextArea } from '../../utils/skill-instructions-textarea'

type FormValues = AiSkillsNodeData['config']

export const getTextSummarizationFields = (
  nodeVars: string[],
  providers?: RProviders,
): GenericFieldConfig<FormValues> => [
  {
    name: 'summary_config.model_id',
    componentType: 'selectWithGroups',
    onChangeListenTo: ['tool_type'],
    zodValidation: invalidSelectValueSchema(
      textSummarizationConfigSchema.shape.model_id,
      providers?.llm_providers?.flatMap((provider) => provider.models.map((model) => model.id)) ??
        [],
    ),
    eventDataExtractor: (value) => value,
    afterOnChange: (value, _, form) => {
      if (!form) return
      const provider = providers?.llm_providers.find((p) =>
        p.models.some((model) => model.id === value.summary_config?.model_id),
      )
      form.setFieldValue('summary_config.provider_id', provider?.id ?? '')
      form.validateAllFields('change')
    },
    inputProps: {
      placeholder: 'Select model',
      label: 'Model',
      groups: providers?.llm_providers?.map((provider) => ({
        label: provider.label,
        options: provider.models.map((model) => ({
          label: (
            <div className="flex items-center gap-2">
              <img src={provider.meta.icon_url} alt={provider.label} className="w-4 h-4" />
              {capitalize(`${model.label}`)}
            </div>
          ),
          searchValue: `${provider.label} ${model.label}`,
          value: model.id,
        })),
      })),
    },
    visible: (state) => state.tool_type === 'summary',
  },
  {
    name: 'summary_config.summary_url',
    componentType: 'input',
    onChangeListenTo: ['tool_type'],
    zodValidation: textSummarizationConfigSchema.shape.summary_url,
    inputProps: {
      inputMode: 'url',
      placeholder: 'Enter summary URL',
      label: 'Summary URL',
      endIcon: (
        <NodevarList nodevars={nodeVars} inputElementSelector="#summary_config.summary_url" />
      ),
    },
    visible: (state) => state.tool_type === 'summary',
  },
  {
    name: 'summary_config.instructions',
    type: 'custom',
    label: 'Additional Instructions',
    onChangeListenTo: ['tool_type'],
    component: SkillInstructionsTextArea,
    eventDataExtractor: (e) => e,
    zodValidation: textSummarizationConfigSchema.shape.instructions,
    inputProps: {
      placeholder: 'ex: Summarize the main points and action items from the provided conversation',
      rows: 4,
      endIcon: (
        <NodevarList
          nodevars={nodeVars}
          inputElementSelector="#summary_config.instructions .ql-ref"
        />
      ),
      'data-testid': 'textarea-skill-instructions',
    },
    visible: (state) => state.tool_type === 'summary',
  },
]
